import {
    useCallback,
    useId,
    useMemo,
    type KeyboardEvent,
    type ReactElement,
    type ReactNode,
} from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Layers } from "lucide-react";
import { Badge } from "@primitives/Badge";
import { ContentSwap } from "@primitives/ContentSwap";
import { motionStyle } from "@primitives/motionStyle";
import { Text } from "@primitives/Text";
import { useViewport } from "@primitives/viewport";
import {
    color,
    layout,
    motion as motionTokens,
    space,
    useListItemMotion,
    usePrefersReducedMotion,
} from "@tokens/index";
import type { WorkflowSummary, WorkItemTally } from "../../experience-shell/workflows";
import { ListEmptyLine, ListError, ListSkeleton } from "./queue-surfaces/ListStates";
import { listRow, listStack, rowMain, rowMeta } from "./queue-surfaces/listSurface";
import { TallyBadge } from "./TallyBadge";
import rowStyles from "./EntityWorkflowList.module.css";

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

export interface EntityWorkflowListProps {
    readonly entityType: string;
    readonly entityId: string;
    readonly entityDisplayName: string;
    readonly items: readonly WorkflowSummary[];
    readonly isLoading: boolean;
    readonly error?: Error | null;
    readonly onSelect: (item: WorkflowSummary) => void;
    /**
     * Per-row live-presence slot. Returns a facepile node for the workflow,
     * or null when nobody is present. A render prop keeps the presence hook
     * in the experience-shell.
     */
    readonly renderPresence?: (item: WorkflowSummary) => ReactNode;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatCreated(iso: string): string {
    const created = new Date(iso);
    return created.toLocaleDateString(undefined, {
        month: "short",
        day: "numeric",
        year: "numeric",
    });
}

interface AggregateTally {
    readonly completed: number;
    readonly open: number;
    readonly pending: number;
    readonly total: number;
    readonly tracked: number;
}

function aggregateTallies(items: readonly WorkflowSummary[]): AggregateTally {
    let completed = 0;
    let open = 0;
    let pending = 0;
    let total = 0;
    let tracked = 0;
    for (const item of items) {
        if (item.workItemTally === null || item.workItemTally === undefined) continue;
        completed += item.workItemTally.completed;
        open += item.workItemTally.open;
        pending += item.workItemTally.pending;
        total += item.workItemTally.total;
        tracked += 1;
    }
    return { completed, open, pending, total, tracked };
}

// ---------------------------------------------------------------------------
// Style sheet
// ---------------------------------------------------------------------------

const styles = {
    root: {
        width: "100%",
        maxWidth: layout.contentMaxWidth,
        display: "flex",
        flexDirection: "column" as const,
        gap: space[5],
        boxSizing: "border-box" as const,
    },
    header: {
        display: "flex",
        flexDirection: "column" as const,
        gap: space[3],
        paddingInline: space[2],
        minWidth: 0,
    },
    headerTopRow: {
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: space[3],
        minWidth: 0,
    },
    headerLeft: {
        display: "flex",
        alignItems: "center",
        gap: space[3],
        minWidth: 0,
        color: color.accent.primary,
    },
    headingWrap: {
        display: "flex",
        flexDirection: "column" as const,
        gap: space[1],
        minWidth: 0,
    },
    aggregateRow: {
        display: "flex",
        alignItems: "center",
        gap: space[3],
        flexWrap: "wrap" as const,
    },
    chips: {
        display: "flex",
        alignItems: "center",
        gap: space[2],
        flexWrap: "wrap" as const,
    },
} as const;

const SKELETON_ROW_COUNT = 4;

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export function EntityWorkflowList({
    entityType,
    entityId,
    entityDisplayName,
    items,
    isLoading,
    error,
    onSelect,
    renderPresence,
}: EntityWorkflowListProps): ReactElement {
    const headingId = useId();
    const { isCompact } = useViewport();

    const aggregate = useMemo(() => aggregateTallies(items), [items]);

    const handleRowKeyDown = useCallback(
        (e: KeyboardEvent<HTMLDivElement>, item: WorkflowSummary) => {
            if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                onSelect(item);
            }
        },
        [onSelect],
    );

    return (
        <section style={styles.root} aria-labelledby={headingId}>
            <header style={styles.header}>
                <div style={styles.headerTopRow}>
                    <div style={styles.headerLeft}>
                        <Layers size={18} strokeWidth={1.75} aria-hidden="true" />
                        <div style={styles.headingWrap}>
                            <Text role="eyebrow" tone="accent">
                                {entityType}
                            </Text>
                            <Text as="h2" role="heading" id={headingId} truncate>
                                {entityDisplayName.length > 0
                                    ? entityDisplayName
                                    : `${entityType} ${entityId}`}
                            </Text>
                            <Text role="mono" size="xs" tone="subtle" truncate>
                                {entityId}
                            </Text>
                        </div>
                    </div>
                    {!isLoading && items.length > 0 ? (
                        <Text role="caption" tone="subtle" tabular style={{ flexShrink: 0 }}>
                            {String(items.length)} workflow{items.length === 1 ? "" : "s"}
                        </Text>
                    ) : null}
                </div>
                {!isLoading && aggregate.tracked > 0 ? (
                    <div
                        style={styles.aggregateRow}
                        aria-label="Aggregate work item tallies for this entity"
                    >
                        <TallyBadge bucket="completed" count={aggregate.completed} />
                        <TallyBadge bucket="open" count={aggregate.open} />
                        <TallyBadge bucket="pending" count={aggregate.pending} />
                        <Text role="caption" tone="subtle" tabular>
                            {String(aggregate.total)} item{aggregate.total === 1 ? "" : "s"} across{" "}
                            {String(aggregate.tracked)} workflow
                            {aggregate.tracked === 1 ? "" : "s"}
                        </Text>
                    </div>
                ) : null}
            </header>

            {error !== null && error !== undefined ? (
                <ListError>
                    Couldn&rsquo;t load workflows for this entity. {error.message}
                </ListError>
            ) : (
                <ContentSwap
                    ready={!isLoading || items.length > 0}
                    skeleton={
                        <ListSkeleton
                            label="Loading workflows for this entity"
                            count={SKELETON_ROW_COUNT}
                            material="filled"
                            leading={0}
                            twoLine
                            trailing={isCompact ? 0 : 160}
                        />
                    }
                >
                    {items.length === 0 ? (
                        <ListEmptyLine>
                            <span>No workflows yet</span>. None are attached to{" "}
                            <Text as="span" role="mono" size="xs" tone="default">
                                {entityType}:{entityId}
                            </Text>
                            .
                        </ListEmptyLine>
                    ) : (
                        <div role="listbox" aria-labelledby={headingId} style={listStack}>
                            <AnimatePresence initial={false}>
                                {items.map((item, index) => (
                                    <Row
                                        key={item.id}
                                        item={item}
                                        index={index}
                                        compact={isCompact}
                                        onSelect={onSelect}
                                        onKeyDown={handleRowKeyDown}
                                        presence={
                                            renderPresence !== undefined ? renderPresence(item) : null
                                        }
                                    />
                                ))}
                            </AnimatePresence>
                        </div>
                    )}
                </ContentSwap>
            )}
        </section>
    );
}

// ---------------------------------------------------------------------------
// Row
// ---------------------------------------------------------------------------

interface RowProps {
    readonly item: WorkflowSummary;
    readonly index: number;
    readonly compact: boolean;
    readonly onSelect: (item: WorkflowSummary) => void;
    readonly onKeyDown: (e: KeyboardEvent<HTMLDivElement>, item: WorkflowSummary) => void;
    readonly presence: ReactNode;
}

function TallyChips({ tally }: { readonly tally: WorkItemTally }): ReactElement {
    return (
        <>
            <TallyBadge bucket="completed" count={tally.completed} />
            <TallyBadge bucket="open" count={tally.open} />
            <TallyBadge bucket="pending" count={tally.pending} />
        </>
    );
}

function Row({ item, index, compact, onSelect, onKeyDown, presence }: RowProps): ReactElement {
    const arrival = useListItemMotion(index, false);
    const reduced = usePrefersReducedMotion();
    const tally = item.workItemTally ?? null;
    const createdBy =
        item.createdByDisplayName !== null &&
        item.createdByDisplayName !== undefined &&
        item.createdByDisplayName.length > 0
            ? item.createdByDisplayName
            : null;

    const pressMotion = reduced
        ? {}
        : {
              whileTap: {
                  scale: motionTokens.press.scale,
                  transition: { duration: motionTokens.press.duration },
              },
          };

    return (
        <motion.div
            role="option"
            aria-selected={false}
            tabIndex={0}
            className={rowStyles.row}
            style={motionStyle({
                ...listRow("filled", { hovered: false, selected: false }),
                background: undefined,
                alignItems: compact ? "flex-start" : "center",
                padding: `${space[3]} ${space[4]}`,
                cursor: "pointer",
            })}
            onClick={() => onSelect(item)}
            onKeyDown={(e: KeyboardEvent<HTMLDivElement>) => onKeyDown(e, item)}
            initial={arrival.initial}
            animate={arrival.animate}
            transition={arrival.transition}
            {...pressMotion}
        >
            <div style={rowMain}>
                <Text role="bodyLg" weight="semibold" truncate>
                    {item.title}
                </Text>
                <div style={rowMeta}>
                    <Badge variant="neutral" size="sm">
                        {item.kind}
                    </Badge>
                    <Text role="caption" tone="muted" tabular>
                        {formatCreated(item.createdAt)}
                    </Text>
                    {createdBy !== null ? (
                        <Text role="caption" tone="muted" truncate>
                            {createdBy}
                        </Text>
                    ) : null}
                </div>
                {compact && tally !== null ? (
                    <div style={styles.chips} data-tallies="stacked">
                        <TallyChips tally={tally} />
                    </div>
                ) : null}
            </div>
            <div
                data-tallies={compact ? undefined : "trailing"}
                style={{
                    display: "flex",
                    alignItems: "center",
                    gap: space[2],
                    flexShrink: 0,
                    minHeight: compact ? undefined : 40,
                }}
            >
                {presence}
                {!compact && tally !== null ? <TallyChips tally={tally} /> : null}
            </div>
        </motion.div>
    );
}
